import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { BehaviorSubject, filter, fromEvent, interval } from 'rxjs';

/** How often a running app checks for a new version. */
const CHECK_INTERVAL_MS = 15 * 60 * 1000;
/** Startup must not hang on a slow or offline network; the update is picked up by a later check. */
const STARTUP_CHECK_TIMEOUT_MS = 5000;

export type ManualUpdateCheckResult = 'updating' | 'up-to-date' | 'failed';

/** The deployed build: the content hash in the main bundle's name (`main-<hash>.js`), or null in an unhashed dev build. */
export function getBuildHash(): string | null {
  const src = document.querySelector<HTMLScriptElement>('script[src*="main-"]')?.src;
  return src?.match(/main-([A-Za-z0-9]+)\.js/)?.[1] ?? null;
}

/**
 * Keeps an installed PWA up to date. The service worker only checks for updates on a (cold) start, and an installed
 * app is mostly resumed from the background instead, so this also checks on resume and periodically.
 * A downloaded update is applied at a moment that doesn't interrupt the user: on resume or on the next navigation.
 */
@Injectable({
  providedIn: 'root',
})
export class ApplicationUpdateService {
  private updateAvailableSubject = new BehaviorSubject<boolean>(false);
  public updateAvailable$ = this.updateAvailableSubject.asObservable();

  private swUpdate = inject(SwUpdate);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  constructor() {
    if ('serviceWorker' in navigator && this.swUpdate.isEnabled) {
      this.listenForUpdates();
    }
  }

  private listenForUpdates(): void {
    this.swUpdate.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        this.updateAvailableSubject.next(true);
      }
    });

    // The cached version is broken (e.g. files of it were evicted) and can't be served anymore: reload from the network.
    this.swUpdate.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => location.reload());

    fromEvent(document, 'visibilitychange')
      .pipe(
        filter(() => document.visibilityState === 'visible'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        // Just reopened, so nothing is in progress: apply a waiting update, otherwise look for one.
        if (this.updateAvailableSubject.value) {
          this.activateUpdate();
        } else {
          this.checkForUpdate();
        }
      });

    interval(CHECK_INTERVAL_MS)
      .pipe(
        filter(() => document.visibilityState === 'visible'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.checkForUpdate());

    // The previous page is left behind anyway, so reloading into the new version (at the new URL) loses nothing.
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd && this.updateAvailableSubject.value),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.activateUpdate());
  }

  private checkForUpdate(): Promise<boolean> {
    return this.swUpdate.checkForUpdate().catch((error) => {
      console.warn('Checking for an application update failed', error);
      return false;
    });
  }

  public activateUpdate(): void {
    this.swUpdate
      .activateUpdate()
      .catch((error) => console.warn('Activating the application update failed', error))
      .finally(() => location.reload());
  }

  /** For a user-triggered check: reloads into a new version when there is one, otherwise reports why not. */
  public async checkNow(): Promise<ManualUpdateCheckResult> {
    if (!this.swUpdate.isEnabled) return 'up-to-date';
    if (this.updateAvailableSubject.value) {
      this.activateUpdate();
      return 'updating';
    }
    try {
      if (await this.swUpdate.checkForUpdate()) {
        this.activateUpdate();
        return 'updating';
      }
      return 'up-to-date';
    } catch (error) {
      console.warn('Checking for an application update failed', error);
      return 'failed';
    }
  }

  /**
   * Call once on app startup. If a new version is available, activates it and
   * reloads — the returned Promise never resolves in that case (page reloads).
   * Safe to call when SW is disabled (no-op), and never rejects or hangs on a bad network.
   */
  public async checkAndApplyIfReady(): Promise<void> {
    if (!this.swUpdate.isEnabled) return;
    const timeout = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), STARTUP_CHECK_TIMEOUT_MS));
    const updateFound = await Promise.race([this.checkForUpdate(), timeout]);
    if (updateFound) {
      await this.swUpdate.activateUpdate().catch(() => undefined);
      location.reload();
      await new Promise<never>(() => {});
    }
  }
}
